import React, { FC, useState } from 'react';
import MuiTableBody from '@mui/material/TableBody';
import MuiTableCell from '@mui/material/TableCell';
import MuiTableRow from '@mui/material/TableRow';
import {
    TableColumnType,
    TableRowType,
    RowActionPropsType,
    CustomRowType,
    SelectRowType,
    CustomRenderType,
    SumColumnsType,
    CustomRowRenderType,
} from 'src/types/table';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import TableRowActions from './TableRowActions';
import Checkbox from '../Checkbox';
import Loading from '../Loading/intex';
import { getRowElementText } from './functions';
import { SxProps, Theme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import Typography from '../Typography';
import translate from '@translate';
import TableSumColumsRow from './TableSumColumsRow';

const EmptyRow = styled('tr')<{ showInModal?: boolean }>(({ theme, showInModal }) => ({
    position: 'relative',

    '& td': {
        height: '120px',
    },

    '& .table-empty-text-column': {
        textAlign: 'center',
        display: 'block',
        position: 'fixed',
        left: showInModal ? '30px' : '270px',
        right: '30px',
        paddingTop: '60px',
    },

    [theme.breakpoints.down('sm')]: {
        '& .table-empty-text-column': {
            left: '0px',
            right: '0px',
        },
    },
}));

interface TableBodyPropsType {
    loading?: boolean;
    rows: TableRowType[];
    allFilteredRows?: TableRowType[];
    rowIdKey: string;
    selectRowIdKey?: string;
    visilbeColumns: TableColumnType[];
    selectOptions?: { [key: string]: ConstantsCodeType[] };
    rowActions?: RowActionPropsType[];
    customRowActions?: RowActionPropsType[];
    customRow?: CustomRowType;
    selectedRows?: SelectRowType[];
    setSelectedRows?: (selectedRows: SelectRowType[]) => void;
    customRender?: CustomRenderType;
    rowSxFce?: (row: TableRowType) => SxProps<Theme>;
    disableFilter?: boolean;
    sumCols?: SumColumnsType;
    sumColsFilter?: (row: TableRowType) => boolean;
    showInModal?: boolean;
    customRowRender?: CustomRowRenderType;
    showClearFilters?: boolean;
}

const TableBody: FC<TableBodyPropsType> = (props) => {
    const [popupOpenRow, setPopupOpenRow] = useState<number | string | undefined>(undefined);

    const handlePopupOpen = (value: boolean, row: TableRowType) => {
        if (!value && !popupOpenRow) {
            return;
        }
        if (value) {
            setPopupOpenRow(row[props.rowIdKey]);
        } else {
            setPopupOpenRow(undefined);
        }
    };

    const handleSelectRow = (row: TableRowType, value: number | boolean) => {
        if (props.setSelectedRows) {
            if (value) {
                props.setSelectedRows([
                    ...(props.selectedRows || []),
                    String(row[props.selectRowIdKey || props.rowIdKey]),
                ]);
            } else {
                props.setSelectedRows(
                    (props.selectedRows || []).filter(
                        (selectItem) => selectItem !== String(row[props.selectRowIdKey || props.rowIdKey])
                    )
                );
            }
        }
    };

    if (props.loading) {
        return (
            <MuiTableBody>
                <tr>
                    <td>
                        <Loading />
                    </td>
                </tr>
            </MuiTableBody>
        );
    }

    return (
        <MuiTableBody>
            {props.customRow && (
                <MuiTableRow>
                    {props.setSelectedRows && <MuiTableCell />}
                    {props.visilbeColumns.map((column) => (
                        <MuiTableCell key={column.key}>{props.customRow && props.customRow(column)}</MuiTableCell>
                    ))}
                    {props.customRowActions && (
                        <MuiTableCell className="row-action">
                            <TableRowActions rowActions={props.customRowActions} row={{}} />
                        </MuiTableCell>
                    )}
                </MuiTableRow>
            )}
            {props.rows.map((row, index) => {
                const rowPopupOpen =
                    popupOpenRow !== undefined && row[props.rowIdKey] === popupOpenRow;
                const rowSelected =
                    props.setSelectedRows &&
                    props.selectedRows?.includes(String(row[props.selectRowIdKey || props.rowIdKey]));

                return (
                    props.customRowRender?.({
                        row,
                        rowSelected,
                        rowPopupOpen,
                        handleSelectRow,
                        handlePopupOpen,
                        setSelectedRows: props.setSelectedRows,
                        rowIdKey: props.rowIdKey,
                        rowSxFce: props.rowSxFce,
                        visilbeColumns: props.visilbeColumns,
                        customRender: props.customRender,
                        selectOptions: props.selectOptions,
                        rowActions: props.rowActions,
                    }) || (
                        <MuiTableRow
                            key={String(row[props.rowIdKey])}
                            sx={props.rowSxFce && props.rowSxFce(row)}
                            className={
                                (rowSelected ? ' table-row-selected' : '') +
                                (rowPopupOpen ? ' table-row-open-popup' : '')
                            }
                        >
                            {props.setSelectedRows && (
                                <MuiTableCell className="row-select">
                                    <Checkbox
                                        value={!!rowSelected}
                                        onChange={(value: number | boolean) => handleSelectRow(row, value)}
                                    />
                                </MuiTableCell>
                            )}
                            {props.visilbeColumns.map((column) => {
                                const { text, element } = getRowElementText(
                                    row,
                                    column,
                                    props.customRender,
                                    props.selectOptions,
                                    { formatDisplay: true }
                                );
                                return (
                                    <MuiTableCell key={column.key} title={text} style={column.customStyle}>
                                        {element || text}
                                    </MuiTableCell>
                                );
                            })}
                            {props.rowActions ? (
                                <MuiTableCell className="row-action">
                                    <TableRowActions
                                        rowActions={props.rowActions}
                                        row={row}
                                        handlePopupOpen={(value) => handlePopupOpen(value, row)}
                                    />
                                </MuiTableCell>
                            ) : props.showClearFilters ? (
                                <MuiTableCell className="row-action" />
                            ) : null}
                        </MuiTableRow>
                    )
                );
            })}
            {!props.disableFilter && !props.rows.length && (
                <EmptyRow showInModal={props.showInModal}>
                    <td></td>
                    <td className="table-empty-text-column">
                        <Typography variant="h3">{translate('table.noResults')}</Typography>
                    </td>
                </EmptyRow>
            )}
            {props.sumCols && (
                <TableSumColumsRow
                    sumCols={props.sumCols}
                    sumColsFilter={props.sumColsFilter}
                    visilbeColumns={props.visilbeColumns}
                    rows={props.allFilteredRows || props.rows}
                    rowActions={props.rowActions}
                    showClearFilters={props.showClearFilters}
                />
            )}
        </MuiTableBody>
    );
};

export default TableBody;
